import React, { useState } from 'react';
import {
  Header,
  Form,
  Button,
  Message,
  Table,
  Segment,
  Input,
  Radio,
  Icon,
  Loader
} from 'semantic-ui-react';
import { contractExcelImportService, ProcessExcelResult, ExcelProcessingError } from '../../services/contracts/contractExcelImportService';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../context/AuthContext';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';

const ContractExcelImportPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [isValidated, setIsValidated] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ProcessExcelResult | null>(null);

  // Verificar se usuário é Admin
  const isAdmin = user?.roles && user.roles.includes('Admin');

  if (!isAdmin) {
    return (
      <GlassPageWrapper title="Importação de Excel" icon="file excel" fillViewport>
        <Message error size="large">
          <Message.Header>Acesso Negado</Message.Header>
          <p>Você não tem permissão para acessar esta página. Apenas administradores podem fazer importação de Excel.</p>
        </Message>
      </GlassPageWrapper>
    );
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const allowedExtensions = ['.xlsx', '.xls'];
      const extension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
      
      if (!allowedExtensions.includes(extension)) {
        showError('Formato inválido. Use arquivos .xlsx ou .xls');
        return;
      }

      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      showError('Selecione um arquivo Excel');
      return;
    }

    setIsProcessing(true);
    setResult(null);

    try {
      const uploadResult = await contractExcelImportService.uploadAndProcessExcel(file, isValidated);
      setResult(uploadResult);

      if (uploadResult.contratosAtualizados > 0) {
        showSuccess(`${uploadResult.contratosAtualizados} contratos atualizados com sucesso!`);
      }

      if (uploadResult.erros > 0 || uploadResult.linhasSemMatch > 0) {
        showError(
          `${uploadResult.erros} erros e ${uploadResult.linhasSemMatch} linhas sem match. ` +
          `Verifique os detalhes abaixo.`
        );
      }

      if (uploadResult.contratosAtualizados === 0 && uploadResult.erros === 0) {
        showSuccess('Arquivo processado, mas nenhum contrato foi atualizado.');
      }
    } catch (error: any) {
      showError(error.response?.data?.message || error.message || 'Erro ao processar arquivo Excel');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <GlassPageWrapper title="Importação de Excel" icon="file excel" fillViewport>
      {isProcessing && (
        <div style={{ textAlign: 'center', padding: '8px 0 12px' }}>
          <Loader active inline size="small" /> Processando arquivo Excel...
        </div>
      )}

      <p style={{ color: '#5a6a7a', margin: '0 0 20px' }}>
        Importação de Excel — atualização de status de contratos
      </p>

        <Segment style={{ background: '#ffffff', padding: '30px', borderRadius: '10px' }}>
          <Form>
            <Form.Field>
              <label>Selecione o arquivo Excel</label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                disabled={isProcessing}
              />
              {file && (
                <Message info style={{ marginTop: '10px' }}>
                  Arquivo selecionado: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
                </Message>
              )}
            </Form.Field>

            <Form.Field>
              <label>Tipo de Excel</label>
              <Form.Group>
                <Form.Field>
                  <Radio
                    label="Não Validados (em andamento)"
                    name="excelType"
                    value="false"
                    checked={!isValidated}
                    onChange={() => setIsValidated(false)}
                    disabled={isProcessing}
                  />
                </Form.Field>
                <Form.Field>
                  <Radio
                    label="Validados (finalizados com sucesso)"
                    name="excelType"
                    value="true"
                    checked={isValidated}
                    onChange={() => setIsValidated(true)}
                    disabled={isProcessing}
                  />
                </Form.Field>
              </Form.Group>
              <Message info style={{ marginTop: '10px' }}>
                Selecione o tipo de Excel: <strong>Não Validados</strong> para produtos em processamento ou{' '}
                <strong>Validados</strong> para produtos já finalizados.
              </Message>
            </Form.Field>

            <Button
              primary
              onClick={handleUpload}
              disabled={!file || isProcessing}
              loading={isProcessing}
              style={{ marginTop: '20px' }}
            >
              <Icon name="upload" />
              Processar Arquivo
            </Button>
          </Form>
        </Segment>

        {result && (
          <Segment style={{ background: '#ffffff', padding: '30px', borderRadius: '10px', marginTop: '20px' }}>
            <Header as="h2">Resultado do Processamento</Header>

            <Message
              info
              style={{
                marginBottom: '20px',
                background: '#e8f4f8',
                border: '1px solid #b3d9e6'
              }}
            >
              <Message.Header>Estatísticas</Message.Header>
              <Message.List>
                <Message.Item>Total de linhas processadas: <strong>{result.totalLinhasProcessadas}</strong></Message.Item>
                <Message.Item>Contratos atualizados: <strong>{result.contratosAtualizados}</strong></Message.Item>
                <Message.Item>Linhas com match: <strong>{result.linhasComMatch}</strong></Message.Item>
                <Message.Item>Linhas sem match: <strong>{result.linhasSemMatch}</strong></Message.Item>
                <Message.Item>Erros: <strong>{result.erros}</strong></Message.Item>
              </Message.List>
            </Message>

            {result.mensagens && result.mensagens.length > 0 && (
              <Message success>
                <Message.Header>Mensagens</Message.Header>
                <Message.List>
                  {result.mensagens.map((msg, idx) => (
                    <Message.Item key={idx}>{msg}</Message.Item>
                  ))}
                </Message.List>
              </Message>
            )}

            {result.errosDetalhados && result.errosDetalhados.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <Header as="h3" color="red">Erros Detalhados</Header>
                <Table celled striped>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Linha Excel</Table.HeaderCell>
                      <Table.HeaderCell>ID Produto</Table.HeaderCell>
                      <Table.HeaderCell>CNPJ/CPF</Table.HeaderCell>
                      <Table.HeaderCell>Oferta</Table.HeaderCell>
                      <Table.HeaderCell>Mensagem</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {result.errosDetalhados.map((error, idx) => (
                      <Table.Row key={idx}>
                        <Table.Cell>{error.linhaExcel}</Table.Cell>
                        <Table.Cell>{error.idProduto || '-'}</Table.Cell>
                        <Table.Cell>{error.cnpjCpf || '-'}</Table.Cell>
                        <Table.Cell>{error.oferta || '-'}</Table.Cell>
                        <Table.Cell>{error.mensagem}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </Segment>
        )}
    </GlassPageWrapper>
  );
};

export default ContractExcelImportPage;

